import React, { createContext, useCallback, useContext, useState, useEffect } from 'react';
import { getAppearance } from '../api/api';

const AppearanceContext = createContext(null);

const defaultAppearance = {
  primary_color: '#2563eb',
  secondary_color: '#16a34a',
  accent_color: '#3b82f6',
  hero_image: 'https://images.pexels.com/photos/6197123/pexels-photo-6197123.jpeg?auto=compress&cs=tinysrgb&w=1920',
  about_image: 'https://images.unsplash.com/photo-1497366858526-0766cadbe8fa?auto=format&fit=crop&w=1000&q=80',
  logo_url: null,
};

/** Éclaircit (percent > 0) ou assombrit (percent < 0) une couleur hexadécimale. */
const adjustColor = (hex, percent) => {
  if (typeof hex !== 'string') return '#2563eb';
  let valeur = hex.replace('#', '');
  if (valeur.length === 3) {
    valeur = valeur
      .split('')
      .map((c) => c + c)
      .join('');
  }
  const num = parseInt(valeur, 16);
  if (Number.isNaN(num)) return hex;

  const ecart = Math.round(2.55 * percent);
  const r = Math.min(255, Math.max(0, (num >> 16) + ecart));
  const v = Math.min(255, Math.max(0, ((num >> 8) & 0x00ff) + ecart));
  const b = Math.min(255, Math.max(0, (num & 0x0000ff) + ecart));
  return '#' + (0x1000000 + r * 0x10000 + v * 0x100 + b).toString(16).slice(1);
};

const applyColors = (colors) => {
  const root = document.documentElement;
  root.style.setProperty('--color-primary', colors.primary_color);
  root.style.setProperty('--color-secondary', colors.secondary_color);
  root.style.setProperty('--color-accent', colors.accent_color);
  root.style.setProperty('--color-primary-dark', adjustColor(colors.primary_color, -20));
  root.style.setProperty('--color-primary-light', adjustColor(colors.primary_color, 40));
};

// Cache local de la dernière apparence connue : appliqué immédiatement au
// chargement du module (avant le premier rendu), ce qui supprime le flash de
// couleur — y compris quand la palette a été personnalisée dans le panel.
const CACHE_KEY = 'netto_appearance';
const lireCache = () => {
  try {
    const brut = localStorage.getItem(CACHE_KEY);
    return brut ? JSON.parse(brut) : null;
  } catch {
    return null;
  }
};
const apparenceInitiale = { ...defaultAppearance, ...(lireCache() || {}) };
applyColors(apparenceInitiale);

export const AppearanceProvider = ({ children }) => {
  const [appearance, setAppearance] = useState(apparenceInitiale);
  const [loading, setLoading] = useState(true);

  const fetchAppearance = useCallback(async () => {
    try {
      const data = await getAppearance();
      // Complète les champs absents par les valeurs par défaut : une image non
      // renseignée ne doit jamais laisser un champ vide côté affichage.
      const fusion = { ...defaultAppearance, ...data };
      setAppearance(fusion);
      applyColors(fusion);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(fusion)); } catch { /* stockage indisponible */ }
    } catch (error) {
      console.error('Chargement de l\'apparence impossible :', error);
      applyColors(defaultAppearance);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAppearance();
  }, [fetchAppearance]);

  return (
    <AppearanceContext.Provider
      value={{ appearance, loading, refreshAppearance: fetchAppearance }}
    >
      {children}
    </AppearanceContext.Provider>
  );
};

export const useAppearance = () => {
  const context = useContext(AppearanceContext);
  if (!context) {
    throw new Error('useAppearance doit être utilisé dans un AppearanceProvider');
  }
  return context;
};
